/**
 * Seul fichier d’entrée SSO FacturX (checklist hub § 2.5).
 * Ne pas recopier ça dans App.tsx ni dans les pages métier.
 *
 * Tuile Pilotage / sso-verify : **Factures** (nom exact en base).
 * Gate métier : GET /facturx/api/ping.php (session + suiteAssertAppAllowed).
 */
import { dismissFacturxBootSplash, markFacturxBootSplashBorn } from "./facturxBootSplash";
import { isFacturxHub2Desk } from "./facturxHub2";
import {
  getFacturxApiBase,
  getSuiteApiBase,
  getWhoamiUrl,
  isEmbeddedInHub,
  isFacturxStandaloneHost,
  readUserId,
  SESSION_ERRORS,
  syncUserIdFromQuery,
} from "./suiteSession";

const APP_DISPLAY_NAME = "Aurélia Factures";
const SUITE_APP_TILE = "Factures";

function hideRoot(): void {
  document.getElementById("root")?.classList.add("hidden");
}

function showRoot(): void {
  document.getElementById("root")?.classList.remove("hidden");
}

function lockShell(): void {
  window.lockSuiteAppShell?.();
  hideRoot();
}

function unlockShell(): void {
  window.unlockSuiteAppShell?.();
  showRoot();
}

function showForbidden(): void {
  dismissFacturxBootSplash();
  window.hideSuiteAuthModal?.();
  lockShell();
  window.showSuiteForbiddenModal?.({
    actions: [
      {
        label: "Actualiser",
        primary: true,
        onClick: () => {
          window.hideSuiteForbiddenModal?.();
          window.location.reload();
        },
      },
    ],
  });
  window.dispatchEvent(new CustomEvent("facturxAuthLost"));
}

function showNeedLogin(options?: { sessionLost?: boolean }): void {
  dismissFacturxBootSplash();
  const sessionLost = options?.sessionLost === true;
  lockShell();
  window.hideSuiteForbiddenModal?.();
  window.showSuiteAuthModal?.(undefined, {
    autoReconnect: !sessionLost,
  });
  window.dispatchEvent(new CustomEvent("facturxAuthLost"));
}

async function fetchSsoVerify(userId: string): Promise<{ ok: boolean; forbidden?: boolean }> {
  const url = `${getSuiteApiBase()}/sso-verify.php?suite_user_id=${encodeURIComponent(userId)}&app=${encodeURIComponent(SUITE_APP_TILE)}`;
  try {
    const res = await fetch(url, { method: "GET", credentials: "include", cache: "no-store" });
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string };
    if (!res.ok || !data.success) {
      const err = data.error ?? "";
      const forbidden =
        res.status === 403 ||
        err === "app_not_permitted" ||
        err === "forbidden_app_permission" ||
        err === "admin_apps_disabled";
      return { ok: false, forbidden };
    }
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

async function fetchPing(userId: string): Promise<{ ok: boolean; forbidden?: boolean }> {
  try {
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), 15000);
    const res = await fetch(`${getFacturxApiBase()}/ping.php`, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      signal: ctrl.signal,
      headers: { "X-User-Id": userId },
    });
    window.clearTimeout(timer);
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean; success?: boolean; error?: string };
    const err = data.error ?? "";
    if (!res.ok || !(data.ok || data.success)) {
      const forbidden =
        res.status === 403 || err === "app_not_permitted" || err === "forbidden_app_permission";
      return { ok: false, forbidden };
    }
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

async function verifyAndEnter(userId: string): Promise<boolean> {
  const sso = await fetchSsoVerify(userId);
  if (!sso.ok) {
    window.__facturxSessionVerified = false;
    if (sso.forbidden) {
      showForbidden();
      return false;
    }
    showNeedLogin({ sessionLost: true });
    return false;
  }
  const ping = await fetchPing(userId);
  if (!ping.ok) {
    window.__facturxSessionVerified = false;
    if (ping.forbidden) {
      showForbidden();
      return false;
    }
    showNeedLogin({ sessionLost: true });
    return false;
  }
  window.__facturxSessionVerified = true;
  unlockShell();
  window.hideSuiteAuthModal?.();
  window.hideSuiteForbiddenModal?.();
  window.startSuiteHubSessionWatch?.();
  dismissFacturxBootSplash();
  window.dispatchEvent(new CustomEvent("facturxAuthReady", { detail: { userId: String(userId) } }));
  return true;
}

function isFacturxDirectOpen(): boolean {
  return isFacturxStandaloneHost() && !isFacturxHub2Desk();
}

function bindFacturxReconnect(): void {
  const btn = document.getElementById("btnOpenAuthPopup");
  if (!btn || btn.dataset.aureliaReconnect === "1") return;
  const fresh = btn.cloneNode(true) as HTMLButtonElement;
  fresh.dataset.aureliaReconnect = "1";
  btn.replaceWith(fresh);
  fresh.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (window.parent !== window && window.getSuiteAuthPopupUrl) {
      const popup = window.open(
        window.getSuiteAuthPopupUrl("facturx"),
        "Aurélia Suite - Connexion",
        "width=520,height=680,scrollbars=yes,resizable=yes",
      );
      if (popup) return;
    }
    window.openSuiteAuthPopup?.("facturx");
  });
}

function showDirectLogin(): void {
  dismissFacturxBootSplash();
  lockShell();
  window.hideSuiteForbiddenModal?.();
  window.stopSuiteIframeSsoPoll?.();
  window.stopSuiteHubSessionWatch?.();
  window.showSuiteAuthModal?.(undefined, { autoReconnect: false });
  window.dispatchEvent(new CustomEvent("facturxAuthLost"));
}

function showHub2Missing(): void {
  showDirectLogin();
}

async function enterFromHub2(hint?: number | string): Promise<boolean> {
  let userId = 0;
  try {
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), 15000);
    const res = await fetch(`${getSuiteApiBase()}/hub2-whoami.php`, {
      credentials: "include",
      cache: "no-store",
      signal: ctrl.signal,
    });
    window.clearTimeout(timer);
    const who = (await res.json().catch(() => null)) as { ok?: boolean; user_id?: number } | null;
    if (res.ok && who?.ok && who.user_id) userId = Number(who.user_id) || 0;
  } catch {
    userId = 0;
  }
  if (!userId) userId = Number(hint) || 0;
  if (!userId) {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromDesk = params.get("suite_user_id") || params.get("userId") || "";
      if (/^\d+$/.test(fromDesk)) userId = Number(fromDesk) || 0;
    } catch {
      userId = 0;
    }
  }
  if (!userId) {
    if (isFacturxDirectOpen()) showDirectLogin();
    else showHub2Missing();
    return false;
  }
  const ping = await fetchPing(String(userId));
  if (!ping.ok) {
    window.__facturxSessionVerified = false;
    if (ping.forbidden) showForbidden();
    else if (isFacturxDirectOpen()) showDirectLogin();
    else showHub2Missing();
    return false;
  }
  window.__facturxHub2UserId = String(userId);
  window.__facturxSessionVerified = true;
  unlockShell();
  window.hideSuiteAuthModal?.();
  window.hideSuiteForbiddenModal?.();
  dismissFacturxBootSplash();
  window.dispatchEvent(new CustomEvent("facturxAuthReady", { detail: { userId: String(userId) } }));
  return true;
}

async function onAuthenticated(userIdArg?: number | string): Promise<void> {
  if (isFacturxHub2Desk() || isFacturxStandaloneHost()) {
    await enterFromHub2(userIdArg);
    return;
  }
  const userId = userIdArg != null ? String(userIdArg) : readUserId();
  if (!userId) return;
  window.persistSuiteUserId?.(userId);
  await verifyAndEnter(userId);
}

function handleSessionLost(): void {
  if (isFacturxHub2Desk()) {
    window.__facturxSessionVerified = false;
    showHub2Missing();
    return;
  }
  if (isFacturxDirectOpen()) {
    window.__facturxSessionVerified = false;
    window.clearSuiteStoredIdentity?.();
    showDirectLogin();
    return;
  }
  window.__facturxSessionVerified = false;
  window.stopSuiteIframeSsoPoll?.();
  window.stopSuiteHubSessionWatch?.();
  window.clearSuiteStoredIdentity?.();
  showNeedLogin({ sessionLost: true });
}

export function facturxNotifyApiAuthError(status: number, error?: string): boolean {
  const err = error ?? "";
  if (status === 403 && (err === "app_not_permitted" || err === "forbidden_app_permission")) {
    showForbidden();
    return true;
  }
  if (status === 401 && SESSION_ERRORS.has(err)) {
    handleSessionLost();
    return true;
  }
  return false;
}

function mountSharedModals(): void {
  window.suiteAuthModalMount?.({ appName: APP_DISPLAY_NAME });
  window.suiteForbiddenModalMount?.({ appName: APP_DISPLAY_NAME });
  window.installSuiteAppShell?.({ shellId: "facturxAppShell" });
  window.installSuiteClearCacheListener?.({ preserveKeys: [] });
}

function usesDeskCookie(): boolean {
  return isFacturxHub2Desk() || isFacturxStandaloneHost();
}

function runSharedInit(): void {
  markFacturxBootSplashBorn();
  mountSharedModals();
  if (isFacturxHub2Desk() || isFacturxStandaloneHost()) {
    if (typeof window.installSuiteAuthPopup === "function") {
      window.installSuiteAuthPopup({
        redirect: "facturx",
        isConnected: () => window.__facturxSessionVerified === true,
        onAuthenticated: (userId) => {
          void onAuthenticated(userId);
        },
        onSessionLost: () => {
          handleSessionLost();
        },
        patchFetch: false,
      });
      bindFacturxReconnect();
    }
    void enterFromHub2();
    return;
  }
  if (typeof window.installSuiteAuthPopup !== "function") {
    console.error("FacturX : kit suite-shared introuvable");
    return;
  }
  window.installSuiteAuthPopup({
    redirect: "facturx",
    isConnected: () => window.__facturxSessionVerified === true,
    onAuthenticated: (userId) => {
      void onAuthenticated(userId);
    },
    onSessionLost: () => {
      handleSessionLost();
    },
    patchFetch: false,
  });
  bindFacturxReconnect();
  const whoamiUrl = getWhoamiUrl();
  if (!usesDeskCookie() && whoamiUrl && typeof window.installSuiteHubSessionWatch === "function") {
    window.installSuiteHubSessionWatch({
      whoamiUrl,
      isEmbeddedInHub,
      hasStoredIdentity: () => Boolean(readUserId()),
      isAppSessionActive: () => window.__facturxSessionVerified === true,
      onSessionLost: () => {
        handleSessionLost();
      },
    });
  }
  void bootAuth();
}

async function bootAuth(): Promise<void> {
  if (usesDeskCookie()) {
    await enterFromHub2();
    return;
  }
  syncUserIdFromQuery();
  lockShell();
  const delay = isEmbeddedInHub() ? 2600 : 800;
  await new Promise((r) => setTimeout(r, delay));
  const userId = readUserId();
  if (userId) {
    await verifyAndEnter(userId);
    return;
  }
  showNeedLogin({ sessionLost: false });
}

if (window.__SUITE_SHARED_READY__) {
  runSharedInit();
} else {
  document.addEventListener("suiteSharedReady", runSharedInit, { once: true });
  document.addEventListener("suiteSharedFailed", () => {
    dismissFacturxBootSplash();
    console.error("FacturX : échec chargement suite-shared");
  });
}
